import { useMemo } from 'react';
import { Card, Collapse, Group, Text, Box, Stack, SimpleGrid } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Calendar, ChevronUp, ChevronDown } from 'react-feather';
import type { TimelineEvent, TimelineEventCategory } from '../services/api/cohortEvents';

interface CohortTimelineProps {
  events: TimelineEvent[];
  totalEvents: number;
  defaultExpanded?: boolean;
}

// Category visual config
const CATEGORY_CONFIG: Record<
  TimelineEventCategory,
  { label: string; borderColor: string; bgColor: string; dotColor: string }
> = {
  coach_event: {
    label: 'Coach Events',
    borderColor: 'var(--mantine-color-warningAmber-5)',
    bgColor: 'var(--mantine-color-warningAmber-0)',
    dotColor: 'var(--mantine-color-warningAmber-5)',
  },
  group_session: {
    label: 'Group Sessions',
    borderColor: 'var(--mantine-color-blue-5)',
    bgColor: 'var(--mantine-color-blue-0)',
    dotColor: 'var(--mantine-color-blue-5)',
  },
  program_milestone: {
    label: 'Program Milestones',
    borderColor: 'var(--mantine-color-medleyGreen-5)',
    bgColor: 'var(--mantine-color-medleyGreen-0)',
    dotColor: 'var(--mantine-color-medleyGreen-5)',
  },
};

// Group events by month_key, preserving order
function groupByMonth(events: TimelineEvent[]): { monthLabel: string; events: TimelineEvent[] }[] {
  const monthMap = new Map<string, { monthLabel: string; events: TimelineEvent[] }>();
  for (const event of events) {
    if (!monthMap.has(event.month_key)) {
      monthMap.set(event.month_key, { monthLabel: event.month_label, events: [] });
    }
    monthMap.get(event.month_key)!.events.push(event);
  }
  return Array.from(monthMap.values());
}

function EventCard({ event }: { event: TimelineEvent }) {
  const config = CATEGORY_CONFIG[event.category];
  return (
    <Box
      p="sm"
      mb="xs"
      style={{
        borderLeft: `3px solid ${config.borderColor}`,
        backgroundColor: config.bgColor,
        borderRadius: 'var(--mantine-radius-sm)',
      }}
    >
      <Text size="sm" fw={600} lh={1.3}>
        {event.name}
      </Text>
      <Text size="xs" c="dimmed" mt={2}>
        {event.date_display}
      </Text>
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Box
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}

export function CohortTimeline({ events, totalEvents, defaultExpanded = false }: CohortTimelineProps) {
  const [opened, { toggle }] = useDisclosure(defaultExpanded);

  const months = useMemo(() => groupByMonth(events), [events]);

  if (events.length === 0) return null;

  return (
    <Card withBorder padding="lg" radius="md">
      {/* Header */}
      <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={toggle}>
        <Group gap="sm">
          <Calendar size={16} color="var(--mantine-color-dimmed)" />
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            Cohort Timeline
          </Text>
          <Text size="xs" c="dimmed">
            ({totalEvents} events)
          </Text>
        </Group>
        {opened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </Group>

      <Collapse in={opened}>
        {/* Legend */}
        <Group gap="lg" mt="md" mb="md">
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <LegendDot key={key} color={config.dotColor} label={config.label} />
          ))}
        </Group>

        {/* Month Grid */}
        <SimpleGrid
          cols={{ base: 1, xs: 2, sm: Math.min(months.length, 4) }}
          spacing="md"
        >
          {months.map((month) => (
            <Box key={month.monthLabel}>
              {/* Month Header */}
              <Box
                py="xs"
                px="sm"
                mb="sm"
                style={{
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  borderRadius: 'var(--mantine-radius-sm)',
                }}
              >
                <Text size="sm" fw={700} ta="center" tt="uppercase">
                  {month.monthLabel}
                </Text>
              </Box>
              {/* Events */}
              <Stack gap={0}>
                {month.events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </Stack>
            </Box>
          ))}
        </SimpleGrid>
      </Collapse>
    </Card>
  );
}

export default CohortTimeline;
